import MiniEvent from "@cdx/common/mini-event";
import type {ScopeGroup} from "@cdx/shared/auth/scopes";
import type {MutationDef} from "../api-utils/types";
import createCollector from "./collector";
import type {Fetcher} from "./collector";
import createCache from "./cache";
import createMutator from "./mutator";
import type {Dispatcher} from "./mutator";
import createInstancePool from "./instance-pool";
import {mergeChanges} from "./Changes";
import type {Changes} from "./Changes";
import type {
  BelongsToDesc,
  CacheVal,
  FieldDependents,
  FieldDesc,
  HasManyDesc,
  ModelDesc,
  ModelDescriptions,
  ModelName,
  RawModelDescription,
} from "./mate-types";

const today = new Date();

/** What an unloaded field of each type reads as; anything else reads as "unknown". */
const defaults: Partial<Record<FieldDesc["type"], unknown>> = {
  array: [],
  json: {},
  date: today,
  int: 0,
  bigint: 0n,
  day: {year: today.getFullYear(), month: today.getMonth() + 1, day: today.getDate()},
  bool: false,
};

const toFieldDesc = (desc: Partial<FieldDesc> = {}): FieldDesc => {
  const preset = desc.type === undefined ? undefined : defaults[desc.type];
  return {
    type: "default",
    defaultValue: preset === undefined ? "unknown" : preset,
    ...desc,
  };
};

const getIdProps = (raw: RawModelDescription): Pick<ModelDesc, "idProp" | "idPropAsArray"> => {
  if (raw.name === "_root") return {idProp: raw.idProp, idPropAsArray: []};
  const {idProp} = raw;
  if (Array.isArray(idProp)) {
    return {idProp: idProp.length === 1 ? idProp[0] : idProp, idPropAsArray: idProp};
  }
  return {idProp: idProp || "id", idPropAsArray: [idProp || "id"]};
};

const normalizeDescription = (raw: RawModelDescription): ModelDesc => {
  const {idProp, idPropAsArray} = getIdProps(raw);

  const fields = Object.fromEntries(
    [...idPropAsArray, ...raw.fields].flatMap((field) => {
      if (typeof field === "string") return [[field, toFieldDesc()] as const];
      return Object.entries(field).map(
        ([label, desc]) => [label, toFieldDesc(typeof desc === "string" ? {} : desc)] as const
      );
    })
  );

  const hasManyFromName = (relName: string): HasManyDesc => ({
    model: relName.replace(/s$/, "") as ModelName,
    isSingleton: false,
    fk: `${raw.name}Id`,
  });
  const hasMany = Object.fromEntries(
    (raw.hasMany || []).flatMap((entry) => {
      if (typeof entry === "string") return [[entry, hasManyFromName(entry)] as const];
      return Object.entries(entry).map(
        ([relName, desc]) => [relName, {...hasManyFromName(relName), ...desc}] as const
      );
    })
  );

  const belongsToFromName = (relName: string): BelongsToDesc => ({
    name: relName,
    model: relName as ModelName,
    fk: `${relName}Id`,
  });
  const belongsTo = Object.fromEntries(
    (raw.belongsTo || []).flatMap((entry) => {
      if (typeof entry === "string") return [[entry, belongsToFromName(entry)] as const];
      return Object.entries(entry).map(
        ([relName, desc]) =>
          [relName, {...belongsToFromName(relName), ...desc, name: relName}] as const
      );
    })
  );
  const fkToBelongsTo = Object.fromEntries(Object.values(belongsTo).map((desc) => [desc.fk, desc]));

  // keys the JSON carries beyond these (guardedBy, scopedTo, ...) stay on the description
  return {...raw, idProp, idPropAsArray, fields, hasMany, belongsTo, fkToBelongsTo};
};

type ChangeSet = Changes["changes"] | "all";

/** `MiniEvent` types every argument alike, and change listeners get an index plus the changes. */
const createChangeEvent = () => {
  const listeners = new Set<(index: number, changes: ChangeSet) => void>();
  return {
    addListener: (cb: (index: number, changes: ChangeSet) => void) => {
      listeners.add(cb);
      return () => {
        listeners.delete(cb);
      };
    },
    emit: (index: number, changes: ChangeSet) => listeners.forEach((l) => l(index, changes)),
  };
};

type CreateApiParams<M extends Record<string, MutationDef>> = {
  descriptions: RawModelDescription[];
  fetcher: Fetcher;
  mutations: M;
  dispatcher: Dispatcher;
  getMissingReadScope?: (modelName: ModelName, group: ScopeGroup) => string | null;
  onCacheClear?: () => void;
  /** fields the server changes when another field is written, see `lib/field-dependents.ts` */
  fieldDependents?: FieldDependents;
};

export default function createApi<M extends Record<string, MutationDef>>({
  descriptions: descriptionList,
  fetcher,
  mutations,
  dispatcher,
  getMissingReadScope,
  onCacheClear,
  fieldDependents = {},
}: CreateApiParams<M>) {
  let notifyUpdatesTimeoutId: ReturnType<typeof setTimeout> | null = null;
  let apiChangeIndex = 0;
  let collectedChanges: ChangeSet | null = null;

  const descriptions = Object.fromEntries(
    descriptionList.map((d) => [d.name, normalizeDescription(d)])
  ) as ModelDescriptions;

  const onChangeEvent = createChangeEvent();

  // only fire once per tick by waiting for one tick before updating the listeners
  function onChange(changes: ChangeSet) {
    if (!collectedChanges) {
      collectedChanges = changes;
    } else {
      if (collectedChanges === "all" || changes === "all") {
        collectedChanges = "all";
      } else {
        mergeChanges(collectedChanges, changes);
      }
    }
    if (!notifyUpdatesTimeoutId) notifyUpdatesTimeoutId = setTimeout(flushChanges);
  }

  function flushChanges() {
    if (notifyUpdatesTimeoutId) clearTimeout(notifyUpdatesTimeoutId);
    notifyUpdatesTimeoutId = null;
    const changesToEmit = collectedChanges;
    collectedChanges = null;
    if (!changesToEmit) return;
    apiChangeIndex += 1;
    onChangeEvent.emit(apiChangeIndex, changesToEmit);
  }

  const onNotLoadedEvent = new MiniEvent<CacheVal["status"]>();

  // TODO: figure out a sane way of who knows about whom how

  const collector = createCollector({
    fetcher,
    addFn: (data) => cache.add(data),
    onNotLoaded: onNotLoadedEvent.emit,
    descriptions,
    getMissingReadScope,
  });
  const cache = createCache(
    descriptions,
    fieldDependents,
    collector,
    onChange,
    onChangeEvent.addListener,
    onCacheClear
  );
  const mutate = createMutator(
    mutations,
    dispatcher,
    cache,
    onChangeEvent.addListener,
    flushChanges
  );
  const instancePool = createInstancePool({
    cache,
    descriptions,
    collect: collector.collect,
    registerOnChangeListener: onChangeEvent.addListener,
    getApiChangeIndex: () => apiChangeIndex,
    notLoadedEvent: onNotLoadedEvent.emit,
  });

  return {
    descriptions,
    cache,
    mutate,
    mutationDescriptions: mutations,
    getModel: instancePool.getInstanceViaId,
    registerOnNotLoadedListener: onNotLoadedEvent.addListener,
    registerOnChangeListener: onChangeEvent.addListener,
    getRoot: instancePool.getRoot,
    getChangeIndex: () => apiChangeIndex,
    isCurrentlyLoading: collector.isCurrentlyLoading,
    triggerOnChange: onChange,
  };
}
